import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { intake, testimonialsPublic } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { useSeo } from "../../lib/useSeo";
import { Avatar } from "../../lib/Avatar";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input, Textarea } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";

const SEGMENTS = [
  {
    title: "Multifamily operators",
    body: "Apartments, condos, senior living — from a single-door landlord to a nationwide portfolio. Replacing AppFolio, Yardi Breeze, or Buildium at a fraction of the per-unit cost.",
  },
  {
    title: "Self-storage operators",
    body: "One facility to nationwide operators. Replacing storEDGE, SiteLink, Easy Storage Solutions, or a spreadsheet — flat from $59/mo instead of $300–500 per facility.",
  },
  {
    title: "Parking + lots",
    body: "Surface lots and garages running monthly permits, retail validation, and transient billing. Operator-side stack instead of a driver-side convenience-fee app.",
  },
  {
    title: "Marinas + RV",
    body: "Marinas, dry stacks, and RV lots with state-specific slip/bay agreements, LOA-bracket pricing, and gated dock or lot access — replacing Marina Office Manager, Speedwell, and paper waivers.",
  },
];

export function CustomersPage() {
  useSeo({
    title: "Customers — NestPlot",
    description: "What real property operators across multifamily, self-storage, parking, and RV/boat say about running on NestPlot.",
    path: "/customers",
  });
  const testimonials = useQuery({
    queryKey: ["public", "testimonials"],
    queryFn: () => testimonialsPublic.list(),
  });
  const hasTestimonials = (testimonials.data ?? []).length > 0;

  return (
    <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="max-w-[680px]">
        <Pill tone="accent" size="sm">
          Operators on NestPlot
        </Pill>
        <h1 className="t-display-xl mt-4">Built for property operators of every size.</h1>
        <p className="text-sub text-lg mt-3">
          NestPlot runs the day-to-day for operators of every size across the
          country — from a single landlord to the largest managers, across
          multifamily, self-storage, parking, and RV/boat.
          Rentals, access, autopay, and state-compliant leases — all on a flat
          monthly price.
        </p>
      </div>

      <section className="mt-12">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Who we serve
        </div>
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SEGMENTS.map((s) => (
            <Card key={s.title}>
              <div className="bg-surface px-5 pt-3 pb-2">
                <Slats color="var(--ink)" opacity={0.4} height={10} />
              </div>
              <div className="px-5 py-5">
                <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
                  {s.title}
                </div>
                <p className="text-sub text-sm mt-2 leading-relaxed">{s.body}</p>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {hasTestimonials && (
        <section className="mt-14">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            In their words
          </div>
          <h2 className="t-display-l mt-2">What operators say.</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {testimonials.data!.map((t) => (
              <Card key={`${t.name}-${t.role}`}>
                <div className="bg-accent px-5 pt-3 pb-2">
                  <Slats color="var(--on-accent)" opacity={0.55} height={10} />
                </div>
                <div className="px-5 py-5">
                  <p className="text-[15px] leading-relaxed">"{t.quote}"</p>
                  <div className="flex items-center gap-3 mt-5">
                    {t.photo_url ? (
                      <img
                        src={t.photo_url}
                        alt={`${t.name}${t.role ? `, ${t.role}` : ""}`}
                        className="h-10 w-10 rounded-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Avatar initials={t.initials} color="var(--accent-2)" />
                    )}
                    <div>
                      <div className="font-semibold">{t.name}</div>
                      <div className="text-xs text-sub">{t.role}</div>
                      {t.location && (
                        <div className="text-xs text-subtle mt-[2px]">{t.location}</div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="mt-14">
        <ListingForm />
      </section>

      <section className="mt-14 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
        <div>
          <h2 className="t-display-l">
            Ready to switch?<br />
            <span className="text-accent">30-day free trial, no card.</span>
          </h2>
          <p className="text-sub mt-3 max-w-[480px]">
            Bring your roster as a CSV, plug in your access vendor, and be
            live in an afternoon. Cancel anytime — your data is yours and
            exports on request.
          </p>
          <div className="mt-5 flex gap-3 flex-wrap">
            <Link to="/signup">
              <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={16} />}>
                Start free trial
              </Btn>
            </Link>
            <Link to="/contact">
              <Btn kind="ghost" size="lg">
                Talk to sales
              </Btn>
            </Link>
          </div>
        </div>
        <Card>
          <div className="bg-ink px-5 pt-3 pb-2">
            <Slats color="var(--accent)" opacity={0.7} height={10} />
          </div>
          <div className="px-5 py-5">
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              The math
            </div>
            <div className="font-display font-black text-[26px] tracking-[-0.025em] mt-1 leading-tight">
              Incumbent − NestPlot = <span className="text-accent">~$300–$900/mo</span>
            </div>
            <p className="text-sub text-sm mt-2">
              Typical monthly savings vs. AppFolio (multifamily), storEDGE
              (self-storage), enterprise parking-ops (lots), or Marina Office
              Manager (marinas) — flat-rate software plus 0% take on
              customer payments. That's another unit, slip, or stall in
              rent every month, kept in your pocket.
            </p>
          </div>
        </Card>
      </section>
    </div>
  );
}

function ListingForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !company.trim()) return;
    setSubmitting(true);
    try {
      const noteParts = [
        `Wants to be listed on /customers.`,
        location.trim() ? `Location: ${location.trim()}.` : "",
        message.trim() ? `Note: ${message.trim()}` : "",
      ].filter(Boolean);
      await intake.submitInquiry(withUtm({
        name: name.trim(),
        email: email.trim(),
        company: company.trim(),
        message: noteParts.join(" "),
        source: "customers/get-listed",
      }));
      setSubmitted(true);
      toast.success("Got it — we'll reach out to set up your listing.");
    } catch {
      toast.error("Send failed. Email info@nestplot.com directly.");
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <Card>
        <div className="px-6 py-8 text-center max-w-[560px] mx-auto">
          <div className="font-display font-extrabold text-[18px] uppercase tracking-[-0.01em]">
            Thanks — you're on the list.
          </div>
          <p className="text-sub text-sm mt-2">
            We'll reach out within one business day to gather your quote, photo,
            and the unit / stall / slip count you'd like to display.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="bg-accent-soft px-5 pt-3 pb-2">
        <Slats color="var(--accent)" opacity={0.7} height={10} />
      </div>
      <div className="px-6 py-6">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          Run on NestPlot?
        </div>
        <h2 className="t-display-l mt-2">Want to get listed here?</h2>
        <p className="text-sub mt-2 max-w-[560px]">
          Tell us about your property — multifamily, self-storage, parking, or
          marina/RV — and we'll set up your listing on this page with a quote,
          photo, and a link to your public sign-up site.
        </p>
        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Input
              label="Property name"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Maple Grove Apartments / Sunset Self Storage / Downtown Garage / Harbor Point Marina"
              required
            />
            <Input
              label="Location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Springfield, IL"
            />
          </div>
          <Textarea
            label="Anything you'd like to share?"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="A short quote you'd be happy to publish, your unit count, or anything else."
            rows={4}
          />
          <div className="flex justify-end">
            <Btn
              kind="primary"
              size="md"
              disabled={submitting}
              iconRight={<Icon name="arrow-right" size={14} />}
            >
              {submitting ? "Sending…" : "Request listing"}
            </Btn>
          </div>
        </form>
      </div>
    </Card>
  );
}
